import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Settings,
  FileText,
  UserCog,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Mail,
  LogOut,
  BarChart3,
  Users,
} from 'lucide-react';

import { cn, showToastMessage, useRoleIds } from '@/utils';
import { NavItem, NavItemComponentProps, UserRole } from '@/types';

import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { logout as clearAuth, RootState } from '@/store';
import { clearUser } from '@/store/slices/userSlice';
import apiSlice from '@/store/api/apiSlice';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { usePermissions } from '@/context/PermissionContext';
const isMobile = window.innerWidth < 768;
interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

type ApiError = {
  data?: {
    message?: string;
  };
};

const isPathActive = (pathname: string, href?: string) => {
  if (!href) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
};

const isDownlinePath = (pathname: string) => /^\/members(\/|$)/.test(pathname);

function NavItemComponent({
  item,
  isOpen,
  level = 0,
  activeMenu,
  setActiveMenu,
  onToggle,
}: NavItemComponentProps) {
  const isExpanded = activeMenu === item.name;
  const location = useLocation();

  const hasChildren = item.children && item.children.length > 0;
  const isDownlineItem = item.permissionKey === 'Users' || item.name === 'Downline';
  const isActive = isDownlineItem
    ? isDownlinePath(location.pathname)
    : isPathActive(location.pathname, item.href);
  const hasActiveChild = Boolean(
    !isDownlineItem && item.children?.some((child) => isPathActive(location.pathname, child.href))
  );
  const shouldHighlight = isActive || hasActiveChild;

  useEffect(() => {
    if (hasActiveChild && isOpen) {
      setActiveMenu(item.name);
    }
  }, [hasActiveChild, isOpen, item.name, setActiveMenu]);

  const handleClick = () => {
    if (hasChildren) {
      if (!isOpen) {
        onToggle?.();
        setActiveMenu(item.name);
        return;
      }

      setActiveMenu(isExpanded ? null : item.name);
    }
  };
  const Icon = item?.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={cn(
            'flex w-full items-center rounded-lg px-3 py-2 text-base font-medium transition-colors',
            shouldHighlight
              ? 'bg-btn-gradient text-card'
              : 'text-gray hover:bg-accent hover:text-accent-foreground',
            !isOpen && 'justify-center'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          title={!isOpen ? item.name : undefined}
        >
          {item.img
            ? item.img && (
                <img
                  src={item.img}
                  className={cn(
                    'h-5 w-5 shrink-0 filter',
                    isOpen && 'mr-3',
                    shouldHighlight ? 'brightness-0 invert' : 'brightness-0'
                  )}
                />
              )
            : Icon && (
                <Icon
                  className={cn(
                    'h-5 w-5 shrink-0 filter',
                    isOpen && 'mr-3',
                    shouldHighlight ? 'brightness-0 invert' : 'brightness-0'
                  )}
                />
              )}

          {isOpen && (
            <>
              <span className="flex-1 text-left text-xs sm:text-sm">{item.name}</span>
              {isExpanded ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </>
          )}
        </button>

        {/* Tree Menu */}
        {isExpanded && isOpen && (
          <div className=" ml-6">
            <div className="relative border-l-2 border-gray  space-y-4 py-2">
              {item.children!.map((child) => (
                <div key={child.name} className="relative">
                  {/* tick */}
                  <span className="absolute -left-0.5 top-2 h-0.5 w-4 bg-gray-600"></span>

                  {child.children ? (
                    <NavItemComponent
                      item={child}
                      isOpen={isOpen}
                      level={level + 1}
                      activeMenu={activeMenu}
                      setActiveMenu={setActiveMenu}
                      onToggle={onToggle}
                    />
                  ) : (
                    <NavLink
                      onClick={() => {
                        if (isMobile) {
                          onToggle?.();
                        }
                      }}
                      to={child.href!}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center text-xs sm:text-sm transition-colors',
                          isActive ? 'text-[#0a2a37] font-medium' : 'text-gray hover:text-green3'
                        )
                      }
                    >
                      - {child.name}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const plainItemShouldHighlight = isDownlineItem
    ? isDownlinePath(location.pathname)
    : isPathActive(location.pathname, item.href);

  return (
    <NavLink
      to={item.href!}
      onClick={() => {
        if (isMobile) {
          onToggle?.();
        }
      }}
      className={() =>
        cn(
          'flex items-center rounded-lg px-3 py-1.5 sm:py-3 text-sm font-medium transition-colors',
          plainItemShouldHighlight
            ? 'bg-btn-gradient text-card border-l-2'
            : 'text-gray hover:bg-accent hover:text-accent-foreground',
          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      {item.img
        ? item.img && (
            <img
              src={item.img}
              className={cn(
                'h-5 w-5 shrink-0 filter',
                isOpen && 'mr-3',
                plainItemShouldHighlight ? 'brightness-0 invert' : 'brightness-0'
              )}
            />
          )
        : Icon && (
            <Icon
              className={cn(
                'h-5 w-5 shrink-0 filter',
                isOpen && 'mr-3',
                plainItemShouldHighlight ? 'brightness-0 invert' : 'brightness-0'
              )}
            />
          )}

      {isOpen && <span className="text-xs sm:text-sm">{item.name}</span>}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const imagekit = import.meta.env.VITE_IMAGEKIT_URL;
  const [logout] = useLogoutMutation();
  const dispatch = useDispatch();

  const { isOwner } = useRoleCheck();
  const { hasModuleAccess, isLoading: isPermissionLoading } = usePermissions();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const { userId } = useRoleIds();
  const userRole = user?.data?.role?.name as UserRole | undefined;
  const DOWNLINE_ROLE_VISIBILITY: Record<UserRole, UserRole[]> = {
    OWNER: ['SUPER MASTER', 'MASTER', 'USER'],
    'SUPER MASTER': ['MASTER', 'USER'],
    MASTER: ['USER'],
    USER: [],
  };

  const visibleRoles: UserRole[] =
    userRole && userRole in DOWNLINE_ROLE_VISIBILITY ? DOWNLINE_ROLE_VISIBILITY[userRole] : [];

  const downlineChildren: NavItem[] = [
    // { name: 'Super Master', href: `/members/${superMasterId}`, role: 'SUPER MASTER' },
    // { name: 'Master', href: `/members/${masterId}`, role: 'MASTER' },
    { name: 'User', href: `/members/${userId}`, role: 'USER' },
    // { name: 'Settlement', href: `/settlement`, role: 'USER' },
  ].filter((item) => visibleRoles.includes(item.role as UserRole));

  const navigation: NavItem[] = React.useMemo(
    () => [
      {
        name: 'Dashboard',
        permissionKey: 'Dashboard',
        href: '/dashboard',
        icon: Home,
        img: `${imagekit}/sidebar/dashboard.svg`,
      },
      {
        name: 'Downline',
        permissionKey: 'Users',
        href: `/members/${userId ?? 4}`,
        icon: BarChart3,
        img: `${imagekit}/sidebar/downline.svg`,
        children: downlineChildren,
      },
      {
        name: 'Risk Management',
        permissionKey: 'RiskManagement',
        href: '/risk',
        icon: Users,
        img: `${imagekit}/sidebar/risk.svg`,
      },
      {
        name: 'My Reports',
        permissionKey: 'MyReport',
        icon: FileText,
        img: `${imagekit}/sidebar/myreport.svg`,
        children: [
          { name: 'Bet List', href: '/bet-list' },
          { name: 'Casino Bet List', href: '/casino-bet-list' },
          // { name: 'P/L Report By Downline', href: '/pl-by-downline' },
          { name: 'P/L Report By Event', href: '/pl-by-event' },
          { name: 'P/L Report By Sports Players', href: '/pl-by-player' },
          // { name: 'P/L Report By Casino Downline', href: '/casinopl-by-downline' },
          { name: 'P/L Report By Casino Players', href: '/casinopl-by-player' },
          { name: 'Business Report', href: '/business-reports' },
          // {
          //   name: 'Inactive Members',
          //   href: '/inactive-members',
          // },
        ],
      },

      {
        name: 'Event Management',
        permissionKey: 'EventManagement',
        img: `${imagekit}/sidebar/event.svg`,
        icon: CreditCard,
        children: [
          { name: 'Events', href: '/event-management' },
          { name: 'Bet Config', href: '/bet-setting' },
        ],
      },

      {
        name: 'Casino Control',
        permissionKey: 'CasinoControl',
        icon: Mail,
        href: '/casino-management',
        img: `${imagekit}/sidebar/casino.svg`,
      },

      {
        name: 'Content Management',
        permissionKey: 'ContentManagement',
        href: '/content-management',
        img: `${imagekit}/sidebar/content.svg`,
        icon: UserCog,
      },

      {
        name: 'Bonus Management',
        permissionKey: 'Bonus',
        icon: Settings,
        img: `${imagekit}/sidebar/bonus.svg`,
        children: [
          {
            name: 'Policy Management',
            href: '/bonus/policy-management',
          },
          {
            name: 'Bonus Statement',
            href: '/bonus/statement',
          },
        ],
      },
      {
        name: 'Banking Management',
        permissionKey: 'BankingManagement',
        icon: Settings,
        img: `${imagekit}/sidebar/bonus.svg`,
        children: [
          {
            name: 'Deposit/Withdraw Request',
            href: '/requests/Credit/all',
          },
          {
            name: 'PG Transactions',
            href: '/payment-gateway/transactions',
          },
          {
            name: 'Manage Accounts',
            href: '/manage-account',
          },
          {
            name: 'PG Management',
            href: '/payment-gateway',
          },
          // {
          //   name: 'Crypto Conversion Settings',
          //   href: '/crypto-conversion',
          // },
        ],
      },

      {
        name: 'Account Statement',
        permissionKey: 'Account Statement',
        icon: Settings,
        href: '/rolewise-account-statement',
      },
      // {
      //   name: 'Account Statement',
      //   icon: Settings,
      //   href: '/account-statement',
      //   img: `${imagekit}/sidebar/account.svg`,
      //   onlyBanker: true,
      // },
      {
        name: 'Export Report History',
        permissionKey: 'Export History',
        icon: Settings,
        href: '/export',
        img: `${imagekit}/sidebar/export.svg`,
      },
      {
        name: 'Change Password',
        // permissionKey: 'Change Password',
        icon: Settings,
        img: `${imagekit}/sidebar/password.svg`,
        href: '/settings',
      },
      // {
      //   name: '2FA',
      //   icon: Settings,
      //   img: `${imagekit}/sidebar/password.svg`,
      //   href: '/2fa',
      // },
      {
        name: 'Roles And Privileges',
        permissionKey: 'Roles-Privileges',
        icon: Mail,
        href: '/roles-and-privileges',
        img: `${imagekit}/sidebar/casino.svg`,
      },
      {
        name: 'Activity Log',
        permissionKey: 'Activity Logs',
        img: `${imagekit}/sidebar/activity.svg`,
        icon: Settings,
        href: '/activity-logs',
      },
      {
        name: 'User IPs',
        permissionKey: 'Activity Logs',
        img: `${imagekit}/sidebar/activity.svg`,
        icon: Settings,
        href: '/user-ips',
      },
    ],
    [downlineChildren, userId, imagekit]
  );

  const filteredNavigation = React.useMemo(() => {
    if (isOwner) {
      return navigation.filter((item) => !item.onlyBanker);
    }

    const hasVisiblePermission = (item: NavItem): NavItem | null => {
      if (item.onlyOwner) return null;
      if (item.onlyBanker) return null;

      // Items without a permissionKey (e.g. Change Password) are always visible.
      const canReadModule = item.permissionKey ? hasModuleAccess(item.permissionKey, 'read') : true;

      if (item.children?.length) {
        const visibleChildren = item.children.filter((child) => {
          const childPermissionKey = child.permissionKey ?? item.permissionKey;
          return childPermissionKey ? hasModuleAccess(childPermissionKey, 'read') : true;
        });

        if (canReadModule || visibleChildren.length > 0) {
          return { ...item, children: canReadModule ? item.children : visibleChildren };
        }

        return null;
      }

      return canReadModule ? item : null;
    };

    if (isPermissionLoading) return [];

    return navigation.map(hasVisiblePermission).filter(Boolean) as NavItem[];
  }, [navigation, isOwner, hasModuleAccess, isPermissionLoading]);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      localStorage.removeItem('transactionCode');
      apiSlice.util.resetApiState();
      showToastMessage('success', 'You have been logged out successfully.');
      navigate('/login');
    } catch (err: unknown) {
      const message =
        typeof err === 'object' &&
        err !== null &&
        'data' in err &&
        typeof (err as ApiError).data?.message === 'string'
          ? ((err as ApiError).data?.message ?? 'Failed to logout. Please try again.')
          : 'Failed to logout. Please try again.';

      showToastMessage('error', message);
    }
  };
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-20 bg-sidebar/20   lg:hidden" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col text-gray bg-sidebar transition-all duration-300 overflow-hidden  border-r border-r-green3',
          isOpen ? 'w-72 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-16 items-center bg-header px-4 py-1">
          <img src={`${imagekit}/login/logo.png`} alt="" className="w-32" />

          {/* Toggle button - desktop only */}
          {/* <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'hidden lg:flex h-6 w-6',
              isOpen ? 'ml-auto' : 'fixed left-2 top-3 z-40 '
            )}
          >
            <reactIcons.squareBack
              className={`w-6 h-6 bg-white text-black p-0.5 rounded-sm transition-all duration-300 ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </Button> */}
        </div>

        {/* Input */}
        {/* <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search..." className="bg-transparent pl-9 text-gray border-green " />
        </div> */}

        {/* Navigation */}
        <nav
          className={cn(
            'flex-1 space-y-1 overflow-y-auto py-1.5 sm:py-3',
            isOpen ? 'px-1.5 sm:px-4' : 'px-1.5'
          )}
        >
          {filteredNavigation?.map((item) => (
            <NavItemComponent
              key={item.name}
              item={item}
              isOpen={isOpen}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              onToggle={onToggle}
            />
          ))}
        </nav>

        {/* Logout */}

        <div
          onClick={handleLogout}
          className={cn(
            'flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray border-t border-t-green3 py-2 sm:py-5 px-2',
            isOpen ? 'gap-3' : 'justify-center'
          )}
          title={!isOpen ? 'Logout' : undefined}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {isOpen && 'Logout'}
        </div>
      </div>
    </>
  );
}
